/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { Typography, Tooltip, Stack, Chip, Divider } from "@mui/material";
import { Grid, Button, Box } from "@mui/material";
import React, { useState } from "react";

import IconButton from "@mui/material/IconButton";

import { useNavigate } from "react-router-dom";

import FlagIcon from "@mui/icons-material/Flag";
import DirectionsIcon from "@mui/icons-material/Directions";
import { Effect, Nullable, WorkOrder, WorkOrderTask } from "../../utils/Interfaces";
import InfoModal from "../../components/modal/InfoModal";
import { getGoogleMapsURL, zipCodeString } from "../../utils/Functions";
import WorkOrderTasks from "../office-work-order/WorkOrderTasks";

export interface TechnicianWorkOrderHeaderProps {
    workOrder: WorkOrder;
    tasks: WorkOrderTask[];
    refreshEntry: Effect;
}

const WorkOrderCard = (props: TechnicianWorkOrderHeaderProps) => {
    const { workOrder, tasks, refreshEntry } = props;

    const [siteCommentsModalOpen, setSiteCommentsModalOpen] = useState(false);

    const nav = useNavigate();

    function formatDate(date: Nullable<Date | string>) {
        if (date === null || date === undefined || date === "") {
            return "--";
        }

        let d = new Date(typeof date === "string" ? new Date(date as string) : date);

        let mins = d.getMinutes();
        let hrs = d.getHours();

        let minsStr: string = mins.toString();

        if (mins < 10) {
            minsStr = `0${mins}`;
        }

        let ampm = "AM";

        if (hrs > 12) {
            hrs -= 12;
            ampm = "PM";
        } else if (hrs === 12) {
            ampm = "PM";
        } else if (hrs === 0) {
            hrs = 12;
        }

        return `${d.toLocaleDateString()} ${hrs}:${minsStr} ${ampm}`;
    }

    return (
        <Card sx={{ minWidth: 275 }} style={{}}>
            <CardContent>
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Box
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography sx={{ fontSize: 25, mb: 1.5 }} color="text.primary">
                            {`Ticket #${workOrder.ticket_no}`}
                        </Typography>
                    </Box>

                    <Box sx={{ marginBottom: "20px" }} />
                </Grid>

                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight="bold">Date Received</Typography>
                    <Typography variant="overline">
                        {`${workOrder.date_received === null || workOrder.date_received === "" ? "--" : formatDate(workOrder.date_received)}`}
                    </Typography>
                </Grid>

                <Box sx={{ marginBottom: "10px" }} />

                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight="bold">Device to be Fixed</Typography>

                    <Typography variant="overline">
                        {workOrder.device_to_be_fixed === null || workOrder.device_to_be_fixed === ""
                            ? "--"
                            : workOrder.device_to_be_fixed}
                    </Typography>
                </Grid>

                <Box sx={{ marginBottom: "10px" }} />

                <Stack direction="row" justifyContent="space-between">
                    <Typography fontWeight="bold">Scissor Lift Available</Typography>

                    <Typography variant="overline">
                        {workOrder.scissor_lift_available ? (
                            <Chip label="Yes" color="success" style={{ fontWeight: "bold" }} />
                        ) : (
                            <Chip label="No" color="error" style={{ fontWeight: "bold" }} />
                        )}
                    </Typography>
                </Stack>

                <Box marginBottom="20px" />

                <Typography fontWeight="bold">Description</Typography>
                <Box marginBottom="10px" />
                <Typography>{workOrder.problem_description !== "" ? workOrder.problem_description : "--"}</Typography>

                <Box marginBottom="20px" />

                <Typography fontWeight="bold">Additional Description</Typography>
                <Box marginBottom="10px" />
                <Typography>{workOrder.additional_description !== "" ? workOrder.additional_description : "--"}</Typography>

                <Box marginBottom="20px" />

                <Typography fontWeight="bold">Work Order Comments</Typography>
                <Box marginBottom="10px" />
                <Typography>
                    <pre>{workOrder.comments !== "" && workOrder.comments !== null ? workOrder.comments : "--"}</pre>
                </Typography>
            </CardContent>

            {workOrder.site && workOrder.site.company && (
                <>
                    <Divider>Service Location</Divider>
                    <CardContent>
                        <Grid container direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                                <Typography display="flex" flexDirection="row" alignItems="center">
                                    {`${workOrder.site.company.name} Store `}&nbsp;
                                    <Typography fontWeight="bold">#{workOrder.site.store_no}</Typography>
                                </Typography>

                                <Typography fontWeight="bold">{`${workOrder.site.street_address}`}</Typography>

                                <Typography fontWeight="bold">{`${workOrder.site.city}, ${workOrder.site.state} ${zipCodeString(workOrder.site)}`}</Typography>
                            </Box>
                            <Stack direction="column">
                                <Button onClick={() => nav(`/work-sites/${workOrder.site.id}`)}>View Site</Button>
                                <Stack direction="row">
                                    <Tooltip title="Get Directions using Google Maps">
                                        <IconButton
                                            color="info"
                                            onClick={() => window.open(getGoogleMapsURL(workOrder.site, "_blank"))}
                                        >
                                            <DirectionsIcon />
                                        </IconButton>
                                    </Tooltip>
                                    {workOrder.site && workOrder.site.comments && (
                                        <Tooltip title="View Site Notes/Comments">
                                            <IconButton color="info" onClick={() => setSiteCommentsModalOpen(true)}>
                                                <FlagIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                            </Stack>
                        </Grid>
                    </CardContent>
                </>
            )}

            <Divider>Work Order Punchlist</Divider>
            <WorkOrderTasks workOrderId={workOrder.id} tasks={tasks} allowTaskEdit={false} additionalAction={refreshEntry} />

            {workOrder.site && (
                <InfoModal
                    title="Site Notes/Comments"
                    description={workOrder.site.comments ?? "--"}
                    open={siteCommentsModalOpen}
                    handleClose={() => setSiteCommentsModalOpen(false)}
                />
            )}
        </Card>
    );
};

export default WorkOrderCard;
